import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AcademicLevelSelector from './components/AcademicLevelSelector';
import MiddleSchoolPathway from './components/MiddleSchoolPathway';
import HighSchoolPathway from './components/HighSchoolPathway';
import UniversityPathway from './components/UniversityPathway';
import DynamicProgressIndicator from './components/DynamicProgressIndicator';
import WelcomeRewards from './components/WelcomeRewards';

const StudentRegistrationFlow = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [academicLevel, setAcademicLevel] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRewards, setShowRewards] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    age: '',
    gender: '',
    region: '',
    academicLevel: '',
    // Middle school specific
    careerInterests: [],
    favoriteActivities: [],
    // High school specific
    currentGrade: '',
    series: '',
    subjects: [],
    academicGoals: [],
    // University specific
    baccalaureateYear: '',
    baccalaureateSeries: '',
    desiredField: '',
    universityPreferences: [],
    previousEducation: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (fieldName, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked
        ? [...prev?.[fieldName], value]
        : prev?.[fieldName]?.filter(item => item !== value)
    }));
    if (errors?.[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const handleAcademicLevelSelect = (level) => {
    setAcademicLevel(level);
    setFormData(prev => ({ ...prev, academicLevel: level }));
    setCurrentStep(2);
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 2) {
      if (!formData?.firstName?.trim()) newErrors.firstName = 'Le prénom est requis';
      if (!formData?.lastName?.trim()) newErrors.lastName = 'Le nom est requis';
      if (!formData?.email?.trim()) {
        newErrors.email = 'L\'email est requis';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Format d\'email invalide';
      }
      if (!formData?.age) newErrors.age = 'L\'âge est requis';
    }

    if (step === 3) {
      if (academicLevel === 'middle-school') {
        if (formData?.careerInterests?.length < 2) {
          newErrors.careerInterests = 'Sélectionnez au moins 2 centres d\'intérêt';
        }
      } else if (academicLevel === 'high-school') {
        if (!formData?.currentGrade) newErrors.currentGrade = 'Le niveau actuel est requis';
        if (formData?.subjects?.length < 3) {
          newErrors.subjects = 'Sélectionnez au moins 3 matières';
        }
      } else if (academicLevel === 'university') {
        if (!formData?.baccalaureateYear) newErrors.baccalaureateYear = 'L\'année du bac est requise';
        if (!formData?.desiredField) newErrors.desiredField = 'Le domaine souhaité est requis';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setAcademicLevel(null);
    } else {
      setCurrentStep(prev => prev - 1);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsSubmitting(true);

    setTimeout(() => {
      console.log('Student registration data:', formData);
      setShowRewards(true);
      setTimeout(() => {
        navigate('/student-dashboard');
      }, 3000);
    }, 2000);
  };

  const getTotalSteps = () => {
    if (!academicLevel) return 3;
    return 3;
  };

  const renderStepContent = () => {
    if (currentStep === 1) {
      return (
        <AcademicLevelSelector
          onSelect={handleAcademicLevelSelect}
          selectedLevel={academicLevel}
        />
      );
    }

    if (currentStep === 2) {
      return (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                <Icon name="User" size={24} color="#FFFFFF" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Informations Personnelles
                </h3>
                <p className="text-sm text-gray-600">
                  Partagez vos informations de base pour personnaliser votre parcours
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prénom <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData?.firstName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Entrez votre prénom"
              />
              {errors?.firstName && (
                <p className="text-sm text-red-500 mt-1">{errors?.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData?.lastName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Entrez votre nom"
              />
              {errors?.lastName && (
                <p className="text-sm text-red-500 mt-1">{errors?.lastName}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="exemple@email.com"
            />
            {errors?.email && (
              <p className="text-sm text-red-500 mt-1">{errors?.email}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Âge <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="age"
                value={formData?.age}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Votre âge"
                min="10"
                max="30"
              />
              {errors?.age && (
                <p className="text-sm text-red-500 mt-1">{errors?.age}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Genre
              </label>
              <select
                name="gender"
                value={formData?.gender}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Sélectionnez</option>
                <option value="male">Masculin</option>
                <option value="female">Féminin</option>
                <option value="other">Autre</option>
              </select>
            </div>
          </div>
        </div>
      );
    }

    if (currentStep === 3) {
      if (academicLevel === 'middle-school') {
        return (
          <MiddleSchoolPathway
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
            onCheckboxChange={handleCheckboxChange}
          />
        );
      } else if (academicLevel === 'high-school') {
        return (
          <HighSchoolPathway
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
            onCheckboxChange={handleCheckboxChange}
          />
        );
      } else if (academicLevel === 'university') {
        return (
          <UniversityPathway
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
            onCheckboxChange={handleCheckboxChange}
          />
        );
      }
    }

    return null;
  };

  if (showRewards) {
    return <WelcomeRewards academicLevel={academicLevel} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="content-area">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-8">
            <button
              onClick={() => navigate('/landing-page')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 mb-6"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm md:text-base">Retour à l'accueil</span>
            </button>

            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Parcours d'Inscription Personnalisé
              </h1>
              <p className="text-base md:text-lg text-gray-600">
                Créez votre profil étudiant adapté à votre niveau académique
              </p>
            </div>

            {academicLevel && (
              <DynamicProgressIndicator
                currentStep={currentStep}
                totalSteps={getTotalSteps()}
                academicLevel={academicLevel}
              />
            )}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 lg:p-10">
            {renderStepContent()}

            {currentStep > 1 && (
              <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-8 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  iconName="ChevronLeft"
                  iconPosition="left"
                  className="w-full sm:w-auto"
                >
                  Précédent
                </Button>

                {currentStep < getTotalSteps() ? (
                  <Button
                    variant="default"
                    onClick={handleNext}
                    iconName="ChevronRight"
                    iconPosition="right"
                    className="w-full sm:flex-1"
                  >
                    Continuer
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    iconName="Check"
                    iconPosition="right"
                    className="w-full sm:flex-1"
                  >
                    {isSubmitting ? 'Création en cours...' : 'Créer Mon Compte'}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentRegistrationFlow;